import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getCheckoutStatus } from '../lib/api';
import { useCart } from '../context/CartContext';

const MAX_POLLS = 8;
const POLL_INTERVAL = 2000;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | paid | expired | failed | error
  const [orderId, setOrderId] = useState(null);
  const [amount, setAmount] = useState(null);
  const [currency, setCurrency] = useState('cad');
  const pollCount = useRef(0);
  const cartCleared = useRef(false);
  const { clear, setIsOpen } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setOrderId(data.order_id);
        if (typeof data.amount_total === 'number') setAmount(data.amount_total / 100);
        if (data.currency) setCurrency(data.currency);

        if (data.payment_status === 'paid') {
          setStatus('paid');
          if (!cartCleared.current) {
            clear();
            setIsOpen(false);
            sessionStorage.removeItem('fpn_last_order_id');
            cartCleared.current = true;
          }
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        if (pollCount.current >= MAX_POLLS) {
          setStatus('timeout');
          return;
        }
        pollCount.current += 1;
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear, setIsOpen]);

  if (status === 'checking') {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#0a0a0a]" />
        <h1 className="text-2xl font-black mt-6">Confirming your payment...</h1>
        <p className="text-neutral-500 mt-2">Please don&apos;t close this window. This may take a few seconds.</p>
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-12 h-12 text-green-600" />
        </div>
        <h1 className="text-3xl md:text-4xl font-black mt-6">Thank you for your order!</h1>
        <p className="text-neutral-600 mt-2">Your payment was successful and your order is being prepared.</p>

        <div className="bg-white border rounded-lg p-6 mt-8 text-left space-y-2 max-w-md mx-auto">
          {orderId && (
            <div className="flex justify-between"><span className="text-neutral-500 text-sm">Order ID</span><span className="font-mono text-sm">#{orderId.slice(0, 8)}</span></div>
          )}
          {amount !== null && (
            <div className="flex justify-between"><span className="text-neutral-500 text-sm">Total charged</span><span className="font-bold">${amount.toFixed(2)} {currency.toUpperCase()}</span></div>
          )}
          <div className="flex justify-between"><span className="text-neutral-500 text-sm">Status</span><span className="font-semibold text-green-700">Paid</span></div>
        </div>

        <div className="bg-neutral-50 rounded-lg p-5 mt-6 text-left max-w-md mx-auto">
          <div className="flex items-start gap-3">
            <Package className="w-5 h-5 text-[#0a0a0a] mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-bold">What happens next?</p>
              <p className="text-neutral-600 mt-1">We&apos;ll prepare your order and contact you within 24 hours. Pick-up available at <strong>3 Taylor Court, Bolton, ON</strong>, or delivery within Bolton.</p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate('/shop')} className="bg-[#0a0a0a] hover:bg-neutral-800">Continue Shopping</Button>
          <Button variant="outline" onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <XCircle className="w-16 h-16 text-orange-500 mx-auto" />
        <h1 className="text-2xl font-black mt-6">Payment session expired</h1>
        <p className="text-neutral-600 mt-2">Your checkout session has expired. Your cart is still saved.</p>
        <Button onClick={() => navigate('/shop')} className="mt-6 bg-[#0a0a0a]">Back to Shop</Button>
      </div>
    );
  }

  if (status === 'timeout') {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <Loader2 className="w-12 h-12 mx-auto text-neutral-400" />
        <h1 className="text-2xl font-black mt-6">Still processing...</h1>
        <p className="text-neutral-600 mt-2">Your payment is taking longer than expected. Please check your email — we&apos;ll send confirmation once it&apos;s complete.</p>
        <Button onClick={() => navigate('/')} className="mt-6 bg-[#0a0a0a]">Back to Home</Button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-20 text-center">
      <XCircle className="w-16 h-16 text-red-500 mx-auto" />
      <h1 className="text-2xl font-black mt-6">Something went wrong</h1>
      <p className="text-neutral-600 mt-2">We couldn&apos;t confirm your payment. Please contact us at <Link to="/contact" className="underline">our contact page</Link> with your session ID.</p>
      {sessionId && <p className="text-xs font-mono text-neutral-400 mt-3">Session: {sessionId.slice(0, 24)}...</p>}
      <Button onClick={() => navigate('/')} className="mt-6 bg-[#0a0a0a]">Back to Home</Button>
    </div>
  );
};

export default CheckoutSuccess;
